import React, { useCallback, useEffect, useState } from 'react';
import { Plus, Edit, Trash2, X, Loader2, Image } from 'lucide-react';
import dayjs from 'dayjs';
import { adminApi, adminErrorMsg } from '../../admin/adminApiClient';
import { usePermissions } from '../../admin/usePermissions';
import { toast } from 'sonner';

const LINK_TYPES = ['event', 'category', 'external_url'];
const EMPTY = { title: '', image_url: '', link_type: 'event', link_target: '', position: 0, is_active: true, valid_from: '', valid_to: '' };

export default function AdminBannersPage() {
  const { has } = usePermissions();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ ...EMPTY });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await adminApi.get('/banners');
      setItems(r.data?.items || r.data || []);
    } catch (e) { toast.error(adminErrorMsg(e)); }
  }, []);

  useEffect(() => { setLoading(true); load().finally(() => setLoading(false)); }, [load]);

  const openCreate = () => { setEditId(null); setForm({ ...EMPTY }); setDrawerOpen(true); };
  const openEdit = (b) => {
    setEditId(b.id);
    setForm({
      title: b.title, image_url: b.image_url, link_type: b.link_type, link_target: b.link_target,
      position: b.position || 0, is_active: b.is_active ?? true,
      valid_from: b.valid_from ? dayjs(b.valid_from).format('YYYY-MM-DDTHH:mm') : '',
      valid_to: b.valid_to ? dayjs(b.valid_to).format('YYYY-MM-DDTHH:mm') : '',
    });
    setDrawerOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.image_url.trim() || !form.link_target.trim()) { toast.error('Title, Image URL, and Link Target are required'); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        title: form.title.trim(),
        image_url: form.image_url.trim(),
        link_target: form.link_target.trim(),
        position: Number(form.position) || 0,
        valid_from: form.valid_from ? new Date(form.valid_from).toISOString() : null,
        valid_to: form.valid_to ? new Date(form.valid_to).toISOString() : null,
      };
      if (editId) {
        await adminApi.patch(`/banners/${editId}`, payload);
        toast.success('Banner updated');
      } else {
        await adminApi.post('/banners', payload);
        toast.success('Banner created');
      }
      setDrawerOpen(false);
      await load();
    } catch (e) { toast.error(adminErrorMsg(e)); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this banner?')) return;
    try { await adminApi.delete(`/banners/${id}`); toast.success('Banner deleted'); await load(); }
    catch (e) { toast.error(adminErrorMsg(e)); }
  };

  return (
    <div data-testid="admin-banners-page">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-xl font-black text-gray-900">Banners</h1>
        {has('banners.create') && (
        <button data-testid="banner-create-btn" onClick={openCreate} className="flex items-center gap-1.5 bg-brand-magenta hover:bg-pink-700 text-white px-4 py-2 rounded-lg text-sm font-bold transition-colors">
          <Plus className="w-4 h-4" /> New Banner
        </button>
        )}
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-brand-magenta" /></div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="banners-table">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50/50">
                  <th className="text-left px-4 py-3 text-[11px] font-bold text-gray-500 uppercase tracking-wider">Image</th>
                  <th className="text-left px-4 py-3 text-[11px] font-bold text-gray-500 uppercase tracking-wider">Title</th>
                  <th className="text-left px-4 py-3 text-[11px] font-bold text-gray-500 uppercase tracking-wider">Position</th>
                  <th className="text-left px-4 py-3 text-[11px] font-bold text-gray-500 uppercase tracking-wider">Link</th>
                  <th className="text-left px-4 py-3 text-[11px] font-bold text-gray-500 uppercase tracking-wider">Active</th>
                  <th className="text-left px-4 py-3 text-[11px] font-bold text-gray-500 uppercase tracking-wider">Valid</th>
                  <th className="text-right px-4 py-3 text-[11px] font-bold text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {items.map((b) => (
                  <tr key={b.id} data-testid={`banner-row-${b.id}`} className="hover:bg-gray-50">
                    <td className="px-4 py-3"><img src={b.image_url} alt="" className="w-20 h-12 object-cover rounded-lg bg-gray-100" /></td>
                    <td className="px-4 py-3 font-semibold text-gray-900">{b.title}</td>
                    <td className="px-4 py-3 text-gray-600">{b.position}</td>
                    <td className="px-4 py-3 text-xs text-gray-600">{b.link_type}: {b.link_target?.slice(0, 20)}</td>
                    <td className="px-4 py-3">
                      <span className={`text-[10px] font-black px-2 py-0.5 rounded-full ${b.is_active ? 'bg-emerald-50 text-emerald-700' : 'bg-gray-100 text-gray-500'}`}>
                        {b.is_active ? 'ACTIVE' : 'OFF'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-500">
                      {b.valid_from ? dayjs(b.valid_from).format('MMM D') : '—'} – {b.valid_to ? dayjs(b.valid_to).format('MMM D') : '—'}
                    </td>
                    <td className="px-4 py-3 text-right flex items-center justify-end gap-1">
                      {has('banners.edit') && <button data-testid={`banner-edit-${b.id}`} onClick={() => openEdit(b)} className="p-1.5 rounded hover:bg-gray-100"><Edit className="w-4 h-4 text-gray-500" /></button>}
                      {has('banners.delete') && <button data-testid={`banner-delete-${b.id}`} onClick={() => remove(b.id)} className="p-1.5 rounded hover:bg-red-50"><Trash2 className="w-4 h-4 text-red-500" /></button>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {items.length === 0 && <p className="text-center text-sm text-gray-400 py-10">No banners</p>}
        </div>
      )}

      {/* Create/Edit Drawer */}
      {drawerOpen && (
        <div className="fixed inset-0 z-50 flex items-start justify-end">
          <div className="absolute inset-0 bg-black/40" onClick={() => setDrawerOpen(false)} />
          <div className="relative bg-white h-full w-full max-w-md overflow-y-auto shadow-xl p-6" data-testid="banner-drawer">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-bold text-gray-900">{editId ? 'Edit Banner' : 'New Banner'}</h3>
              <button onClick={() => setDrawerOpen(false)} className="p-1 rounded hover:bg-gray-100"><X className="w-5 h-5" /></button>
            </div>
            <form onSubmit={save} className="flex flex-col gap-3">
              <Field label="Title"><input data-testid="banner-title-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="input-field" /></Field>
              <Field label="Image URL"><input data-testid="banner-imageurl-input" value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} className="input-field" /></Field>
              {form.image_url && <img src={form.image_url} alt="Preview" className="w-full h-32 object-cover rounded-lg bg-gray-100" />}
              <Field label="Link Type">
                <select data-testid="banner-linktype-input" value={form.link_type} onChange={(e) => setForm({ ...form, link_type: e.target.value })} className="input-field">
                  {LINK_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </Field>
              <Field label="Link Target"><input data-testid="banner-linktarget-input" value={form.link_target} onChange={(e) => setForm({ ...form, link_target: e.target.value })} className="input-field" placeholder="Event ID, category name, or URL" /></Field>
              <Field label="Position"><input data-testid="banner-position-input" type="number" value={form.position} onChange={(e) => setForm({ ...form, position: e.target.value })} className="input-field" /></Field>
              <label className="flex items-center gap-2 cursor-pointer">
                <input data-testid="banner-active-input" type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} className="w-4 h-4 rounded border-gray-300 text-brand-magenta focus:ring-pink-500" />
                <span className="text-sm font-semibold text-gray-700">Active</span>
              </label>
              <Field label="Valid From"><input data-testid="banner-validfrom-input" type="datetime-local" value={form.valid_from} onChange={(e) => setForm({ ...form, valid_from: e.target.value })} className="input-field" /></Field>
              <Field label="Valid To"><input data-testid="banner-validto-input" type="datetime-local" value={form.valid_to} onChange={(e) => setForm({ ...form, valid_to: e.target.value })} className="input-field" /></Field>
              <button data-testid="banner-save-btn" type="submit" disabled={saving} className="w-full bg-brand-magenta hover:bg-pink-700 disabled:opacity-60 text-white py-3 rounded-lg font-bold text-sm transition-colors mt-2">
                {saving ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : (editId ? 'Update' : 'Create')}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="flex flex-col gap-1">
      <label className="text-xs font-bold text-gray-500">{label}</label>
      {children}
    </div>
  );
}
